export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly actualUpdateId?: number,
    readonly retryAfterMs?: number,
  ) {
    super(message);
  }
}

let onUnauthorized: (() => void) | null = null;

/** Called on any 401 outside the auth endpoints (e.g. session expiry). */
export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  const response = await fetch(url, {
    method,
    credentials: "same-origin",
    headers: body !== undefined ? { "content-type": "application/json" } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (response.status === 401 && !url.startsWith("/api/auth/") && url !== "/api/me") {
    onUnauthorized?.();
  }
  if (!response.ok) {
    let data: { error?: string; code?: string; actualUpdateId?: number; retryAfterMs?: number } = {};
    try {
      data = (await response.json()) as typeof data;
    } catch {
      // non-JSON error body
    }
    throw new ApiError(
      response.status,
      data.code ?? "error",
      data.error ?? response.statusText,
      data.actualUpdateId,
      data.retryAfterMs,
    );
  }
  return (await response.json()) as T;
}

export const api = {
  get: <T>(url: string) => request<T>("GET", url),
  post: <T>(url: string, body?: unknown) => request<T>("POST", url, body),
  put: <T>(url: string, body?: unknown) => request<T>("PUT", url, body),
  delete: <T>(url: string) => request<T>("DELETE", url),
};

export function qs(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") {
      search.set(key, String(value));
    }
  }
  const encoded = search.toString();
  return encoded ? `?${encoded}` : "";
}
